import type { BackfillStatus, BackfillStep } from '@mesa/core/types';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Loader2Icon, CheckCircle2Icon, CircleIcon, ArrowRightIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import mesaLogo from '@/assets/logo-bw-transparent.svg';
import approachingMesa from '@/assets/onboarding-1.webp';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useZero } from '@/lib/zero';
import { installationsQuery, setCurrentOrgStorage, useCurrentOrgMutators } from '@/services/org';

const installCallbackSchema = z.object({
  installation_id: z.coerce.number(),
  state: z.string().optional(),
  setup_action: z.enum(['install', 'request', 'update']),
});

export const Route = createFileRoute('/_authed/installation/callback')({
  validateSearch: zodValidator(installCallbackSchema),
  loaderDeps: ({ search: { installation_id, state, setup_action } }) => ({ installation_id, state, setup_action }),
  loader: async ({ context: { queryClient }, deps: { installation_id, state, setup_action } }) => {
    const stateData = state
      ? z.object({ redirectUrl: z.string(), origin: z.string() }).parse(JSON.parse(atob(state)))
      : null;
    queryClient.prefetchQuery(installationsQuery());
    return { installation_id, stateData, setup_action };
  },
  component: InstallationCallback,
});

function InstallationCallback() {
  const zero = useZero();
  const { installation_id, stateData, setup_action } = Route.useLoaderData();

  const { data: installations } = useQuery(installationsQuery());
  const matchedInstallation = useMemo(
    () => installations?.find((i) => i.id === installation_id),
    [installations, installation_id]
  );

  const { setCurrentOrg } = useCurrentOrgMutators();
  useEffect(() => {
    console.log('matchedInstallation', matchedInstallation);
    if (matchedInstallation) {
      console.log('setting current org', matchedInstallation);
      setCurrentOrg(matchedInstallation);
    }
  }, [matchedInstallation]);

  const navigate = useNavigate();

  if (setup_action === 'update' && matchedInstallation) {
    navigate({ to: '/pulls' });
  }

  const randomLoadingStatements = [
    'Unfurling the schematics...',
    'Firing up the worker bots...',
    'Drinking coffee...',
    'Procuring giblets...',
    'Thinking hard...',
  ];

  return (
    <div
      className="flex items-center justify-center h-full"
      style={{
        backgroundImage: `url('${approachingMesa}')`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundColor: '#000',
      }}
    >
      <Card className="h-[80vh] max-h-[500px] w-[400px] overflow-auto pb-0 bg-gradient-to-t from-amber-50 via-stone-50 to-stone-50/80">
        {!matchedInstallation && (
          <div className="flex items-center justify-center h-full">
            {randomLoadingStatements[Math.floor(Math.random() * randomLoadingStatements.length)]}
            <Loader2Icon className="h-4 w-4 animate-spin" />
          </div>
        )}
        {matchedInstallation && (
          <>
            <CardHeader></CardHeader>
            <CardContent className="flex flex-col items-center justify-center h-full space-y-2 relative gap-4 mb-16">
              <img src={mesaLogo} alt="Mesa Valley" className="size-16" />
              <div className="flex flex-col items-center justify-center gap-2">
                <h2 className="text-3xl font-bold text-stone-700 text-center font-[Arizona]">Welcome to the Mesa</h2>
                <p className="text-sm text-stone-500 text-center">
                  Let's start by setting up {matchedInstallation.account.name || matchedInstallation.account.login}
                </p>
              </div>
              <Button
                className="bg-amber-700 hover:bg-amber-800 text-white p-5 text-md rounded-full flex items-center justify-center gap-1 mt-8"
                onClick={() =>
                  navigate({ to: '/onboarding/$owner', params: { owner: matchedInstallation.account.login } })
                }
              >
                Get Started
              </Button>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
